import {Card, CardBody, Row, Col} from "reactstrap";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";
const TermsAndConditions = () => {
  return (
    <BackgroundColorContext.Consumer>
      {() => (
        <>
          <Row>
            <Col md="12">
              <Card>
                <CardBody className="card-copy">
                  <h1>Terms of Service</h1>
                  <p>
                    <strong>Last updated: October 15, 2025</strong>
                  </p>
                  <p>
                    Please read these Terms of Service (&quot;Terms&quot;, &quot;Terms of Service&quot;) carefully
                    before using the TrainerDX service operated by us (&quot;Service&quot;, &quot;we&quot;,
                    &quot;us&quot;, or &quot;our&quot;).
                  </p>

                  <h2>1. Acceptance of Terms</h2>
                  <p>
                    By accessing or using trainerdx.com (&quot;the Website&quot; or &quot;the Service&quot;), you agree
                    to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms,
                    you may not access or use the Service.
                  </p>
                  <p>
                    These Terms constitute a legally binding agreement between you and Torque Advisory LLC, a Washington
                    State limited liability company (&quot;Torque Advisory&quot;, &quot;we&quot;, &quot;us&quot;, or
                    &quot;our&quot;), the operator of the TrainerDX service.
                  </p>

                  <h2>2. Definitions</h2>
                  <ul>
                    <li>
                      <strong>&quot;Account&quot;</strong> means your registered user account on the Service.
                    </li>
                    <li>
                      <strong>&quot;Advanced Analysis Tokens&quot;</strong> or <strong>&quot;Tokens&quot;</strong> means
                      the digital credits you purchase to access premium AI-powered analysis features.
                    </li>
                    <li>
                      <strong>&quot;Analysis&quot;</strong> means the processing and evaluation of uploaded log files to
                      generate performance reports and recommendations.
                    </li>
                    <li>
                      <strong>&quot;Content&quot;</strong> means any data, text, files, information, usernames, images,
                      graphics, photos, profiles, works of authorship, applications, links and other content or
                      materials you upload to the Service.
                    </li>
                    <li>
                      <strong>&quot;Free Tier&quot;</strong> means the free service level that includes 5 Advanced
                      Analysis Tokens per month.
                    </li>
                    <li>
                      <strong>&quot;Log Files&quot;</strong> means ANT+ device log files or other supported file formats
                      you upload for analysis.
                    </li>
                    <li>
                      <strong>&quot;Paid Services&quot;</strong> means premium features that require payment, including
                      the purchase of Advanced Analysis Tokens beyond the Free Tier allocation.
                    </li>
                    <li>
                      <strong>&quot;You&quot;</strong> or <strong>&quot;User&quot;</strong> means the individual person,
                      company, or organization that accesses or uses the Service.
                    </li>
                  </ul>

                  <h2>3. Eligibility and Account Registration</h2>
                  <h3>3.1 Age Requirements</h3>
                  <p>
                    You must be at least 13 years old to use this Service. If you are between 13 and 18 years old (or
                    the age of majority in your jurisdiction), you may only use the Service with the consent and under
                    the supervision of a parent or legal guardian who agrees to be bound by these Terms.
                  </p>
                  <p>
                    To purchase Paid Services, you must be at least 18 years old or have permission from a parent or
                    legal guardian who is a valid credit card holder.
                  </p>
                  <h3>3.2 Account Creation</h3>
                  <p>
                    To access certain features, you must create an Account. You agree to provide accurate, current, and
                    complete information during registration and to update such information to keep it accurate,
                    current, and complete.
                  </p>
                  <h3>3.3 Account Security</h3>
                  <p>
                    You are responsible for maintaining the confidentiality of your Account credentials and for all
                    activities that occur under your Account. You agree to notify us immediately of any unauthorized use
                    of your Account. We are not liable for any loss or damage arising from your failure to protect your
                    Account credentials.
                  </p>
                  <h3>3.4 Geographic Restrictions</h3>
                  <p>
                    The Service is not available to users located in, or residents of, the following countries: North
                    Korea, China, and Russia. By using the Service, you represent and warrant that you are not located
                    in, under the control of, or a national or resident of any such restricted country.
                  </p>

                  <h2>4. Service Description</h2>
                  <h3>4.1 Core Service</h3>
                  <p>
                    TrainerDX provides analysis services for uploaded log files from fitness and gaming applications,
                    generating reports on performance metrics, network connectivity, sensor data, and hardware
                    compatibility.
                  </p>
                  <h3>4.2 Free Tier</h3>
                  <p>All users receive a Free Tier that includes:</p>
                  <ul>
                    <li>5 Advanced Analysis Tokens per month</li>
                    <li>Access to basic analysis features</li>
                    <li>File upload and storage (retained for 7 days)</li>
                    <li>Analysis result viewing (retained indefinitely)</li>
                  </ul>
                  <p>
                    Free Tier tokens expire at the end of each calendar month and do not roll over. A new allocation of
                    5 tokens is provided on the first day of each month.
                  </p>
                  <h3>4.3 Paid Services</h3>
                  <p>
                    Users may purchase additional Advanced Analysis Tokens beyond the Free Tier allocation. Each
                    Advanced Analysis consumes one (1) token. Purchased tokens do not expire.
                  </p>

                  <h2>5. Pricing and Payment</h2>
                  <h3>5.1 Token Packages</h3>
                  <p>Advanced Analysis Tokens are available for purchase in the following packages:</p>
                  <ul>
                    <li>
                      <strong>Starter Pack:</strong> 10 tokens
                    </li>
                    <li>
                      <strong>Value Pack:</strong> 30 tokens
                    </li>
                    <li>
                      <strong>Power Pack:</strong> 100 tokens
                    </li>
                  </ul>
                  <p>
                    Current pricing is displayed on our website and is subject to change. Promotional pricing, including
                    introductory discounts, may be offered from time to time and may expire without notice.
                  </p>
                  <h3>5.2 Payment Processing</h3>
                  <p>
                    All payments are processed securely through Stripe, a third-party payment processor. By providing
                    payment information, you represent that you are authorized to use the payment method provided. We
                    accept major credit and debit cards. All prices are in United States Dollars (USD).
                  </p>
                  <h3>5.3 Sales Tax</h3>
                  <p>
                    For customers located in Washington State, USA, applicable state and local sales taxes will be added
                    to your purchase at checkout as required by law. Prices displayed may be exclusive of taxes.
                    International customers may be subject to taxes, duties, and fees imposed by their local
                    jurisdiction, which are the customer's responsibility.
                  </p>
                  <h3>5.4 Token Consumption</h3>
                  <p>
                    Advanced Analysis Tokens are consumed when you initiate an Advanced Analysis request. Token
                    consumption occurs server-side before analysis begins. Tokens are deducted from your account balance
                    immediately upon request submission.
                  </p>
                  <h3>5.5 Order Confirmation</h3>
                  <p>
                    Upon successful purchase, you will receive an order confirmation via email and your Account balance
                    will be updated immediately. Purchased tokens are non-transferable and may only be used by the
                    Account holder who purchased them.
                  </p>

                  <h2>6. Refund Policy</h2>
                  <h3>6.1 Automatic Refunds</h3>
                  <p>Tokens are automatically refunded to your Account in the following circumstances:</p>
                  <ul>
                    <li>
                      <strong>Service Errors:</strong> If an Advanced Analysis fails due to a server error (HTTP 500
                      status), the consumed token will be automatically refunded to your Account balance.
                    </li>
                    <li>
                      <strong>Processing Failures:</strong> If the analysis service is unable to complete processing due
                      to technical issues on our end.
                    </li>
                  </ul>
                  <h3>6.2 No Refunds</h3>
                  <p>Tokens will NOT be refunded in the following circumstances:</p>
                  <ul>
                    <li>
                      <strong>User Errors:</strong> Invalid log file uploads, missing required data, or other
                      user-caused errors (HTTP 400, 404 status codes).
                    </li>
                    <li>
                      <strong>Result Dissatisfaction:</strong> Dissatisfaction with analysis results, recommendations,
                      or report content.
                    </li>
                    <li>
                      <strong>Unused Tokens:</strong> Unused tokens remaining in your Account after purchase.
                    </li>
                    <li>
                      <strong>Expired Free Tier Tokens:</strong> Free Tier tokens that expire at month-end.
                    </li>
                  </ul>
                  <h3>6.3 Payment Disputes</h3>
                  <p>
                    All sales are final. If you believe you have been incorrectly charged, please contact us at
                    support@trainerdx.com within 30 days of the charge. We will investigate and resolve genuine billing
                    errors.
                  </p>
                  <h3>6.4 Chargebacks</h3>
                  <p>
                    Initiating a chargeback or payment dispute without first attempting to resolve the matter with us
                    may result in immediate termination of your Account and forfeiture of all tokens.
                  </p>

                  <h2>7. User Content and Data</h2>
                  <h3>7.1 Content Ownership</h3>
                  <p>
                    You retain all ownership rights to the Log Files and other Content you upload to the Service. By
                    uploading Content, you grant us a limited, non-exclusive, royalty-free license to store, process,
                    analyze, and display your Content solely for the purpose of providing the Service to you.
                  </p>
                  <h3>7.2 Analysis Results</h3>
                  <p>
                    Analysis results, reports, and recommendations generated by the Service (&quot;Generated
                    Content&quot;) are provided to you for your personal use. You may download, save, and share
                    Generated Content as you see fit.
                  </p>
                  <h3>7.3 Data Retention</h3>
                  <ul>
                    <li>
                      <strong>Uploaded Log Files:</strong> Retained for 7 days from upload date, then automatically
                      deleted.
                    </li>
                    <li>
                      <strong>Analysis Results:</strong> Retained indefinitely unless you delete your Account or request
                      deletion.
                    </li>
                    <li>
                      <strong>Account Data:</strong> Retained for the duration of your Account existence.
                    </li>
                  </ul>
                  <h3>7.4 Data Deletion</h3>
                  <p>
                    Upon Account deletion or termination, all associated data including uploaded files, analysis
                    results, and personal information will be deleted from our systems within 30 days, except as
                    required for legal or regulatory compliance.
                  </p>
                  <h3>7.5 Prohibited Content</h3>
                  <p>You agree not to upload or transmit any Content that:</p>
                  <ul>
                    <li>Contains viruses, malware, or malicious code</li>
                    <li>Violates any intellectual property rights</li>
                    <li>Contains personal information of others without consent</li>
                    <li>Is unlawful, harmful, threatening, abusive, or otherwise objectionable</li>
                    <li>Impersonates any person or entity</li>
                  </ul>

                  <h2>8. Third-Party Services</h2>
                  <h3>8.1 Service Providers</h3>
                  <p>The Service uses the following third-party service providers:</p>
                  <ul>
                    <li>
                      <strong>Amazon Web Services (AWS):</strong> Cloud hosting, storage, and computing infrastructure
                    </li>
                    <li>
                      <strong>Stripe:</strong> Payment processing
                    </li>
                    <li>
                      <strong>Google Analytics:</strong> Anonymous usage analytics (no personally identifiable
                      information or log file data)
                    </li>
                  </ul>
                  <p>
                    Your use of the Service constitutes consent to the processing of your data by these third-party
                    providers in accordance with their respective privacy policies and our Privacy Policy.
                  </p>
                  <h3>8.2 Third-Party Links</h3>
                  <p>
                    The Service may contain links to third-party websites or services. We are not responsible for the
                    content, privacy policies, or practices of third-party sites. You access such sites at your own
                    risk.
                  </p>

                  <h2>9. Acceptable Use Policy</h2>
                  <p>You agree NOT to:</p>
                  <ul>
                    <li>Use the Service for any unlawful purpose or in violation of these Terms</li>
                    <li>Attempt to gain unauthorized access to the Service, other Accounts, or computer systems</li>
                    <li>
                      Use automated scripts, bots, or tools to access the Service without express written permission
                    </li>
                    <li>Interfere with or disrupt the Service or servers/networks connected to the Service</li>
                    <li>Reverse engineer, decompile, or disassemble any aspect of the Service</li>
                    <li>Upload excessively large files or make excessive requests designed to burden our systems</li>
                    <li>Resell, redistribute, or commercialize the Service or Generated Content</li>
                    <li>Share Account credentials with others or maintain multiple Accounts for the same individual</li>
                    <li>Use the Service to harass, abuse, or harm another person or entity</li>
                    <li>
                      Attempt to manipulate token balances, exploit pricing errors, or engage in fraudulent payment
                      activity
                    </li>
                  </ul>

                  <h2>10. Intellectual Property Rights</h2>
                  <h3>10.1 Service Ownership</h3>
                  <p>
                    The Service, including its software, design, text, graphics, logos, button icons, images, audio
                    clips, data compilations, and all other content (excluding User Content), is owned by Torque
                    Advisory LLC or our licensors and is protected by United States and international copyright, patent,
                    trade secret, and other intellectual property laws.
                  </p>
                  <h3>10.2 Limited License</h3>
                  <p>
                    Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license
                    to access and use the Service for your personal, non-commercial use.
                  </p>
                  <h3>10.3 Third-Party Marks</h3>
                  <p>
                    All trademarks, service marks, trade names, and logos displayed on the Service are the property of
                    their respective owners. Nothing in these Terms grants you any right to use any trademark, service
                    mark, trade name, or logo without the express written permission of the owner.
                  </p>
                  <h3>10.4 Feedback</h3>
                  <p>
                    If you provide us with feedback, suggestions, or ideas about the Service, you grant us an
                    unrestricted, perpetual, irrevocable, royalty-free license to use, modify, and incorporate such
                    feedback without compensation or attribution.
                  </p>

                  <h2>11. Account Termination and Suspension</h2>
                  <h3>11.1 Termination by You</h3>
                  <p>
                    You may terminate your Account at any time by contacting us at support@trainerdx.com or using the
                    account deletion feature within the Service. Upon termination, any unused tokens (including
                    purchased tokens) will be forfeited without refund.
                  </p>
                  <h3>11.2 Termination by Us</h3>
                  <p>
                    We reserve the right to suspend or terminate your Account and access to the Service, immediately and
                    without prior notice, for any reason, including but not limited to:
                  </p>
                  <ul>
                    <li>Violation of these Terms or our Acceptable Use Policy</li>
                    <li>Fraudulent payment activity or chargebacks</li>
                    <li>Abusive or harassing behavior toward our staff or other users</li>
                    <li>Use of the Service in a manner that causes harm or risk to the Service or others</li>
                    <li>Non-payment of fees or unauthorized use of Paid Services</li>
                    <li>At our sole discretion for any other reason</li>
                  </ul>
                  <h3>11.3 Effect of Termination</h3>
                  <p>Upon termination or suspension of your Account:</p>
                  <ul>
                    <li>Your right to access and use the Service will immediately cease</li>
                    <li>All unused tokens (including purchased tokens) will be forfeited without refund</li>
                    <li>Your data will be deleted in accordance with our Data Retention policy (Section 7.3)</li>
                    <li>Sections of these Terms that by their nature should survive will remain in effect</li>
                  </ul>

                  <h2>12. Disclaimers and Warranties</h2>
                  <h3>12.1 &quot;AS IS&quot; Service</h3>
                  <p>
                    THE SERVICE IS PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS WITHOUT
                    WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES
                    OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, OR WARRANTIES ARISING
                    FROM COURSE OF DEALING OR USAGE OF TRADE.
                  </p>
                  <h3>12.2 No Guarantee of Accuracy</h3>
                  <p>
                    We do not warrant that the analysis results, recommendations, or Generated Content will be accurate,
                    complete, reliable, current, or error-free. Analysis results are provided for informational purposes
                    only and should not be relied upon as the sole basis for making decisions.
                  </p>
                  <h3>12.3 No Uptime Guarantee</h3>
                  <p>
                    We do not guarantee that the Service will be uninterrupted, timely, secure, or error-free. We do not
                    guarantee any specific uptime percentage or availability.
                  </p>
                  <h3>12.4 Third-Party Services</h3>
                  <p>
                    We are not responsible for the availability, performance, or actions of third-party service
                    providers (including AWS, Stripe, or payment networks). Service interruptions or errors caused by
                    third parties are beyond our control.
                  </p>
                  <h3>12.5 AI-Generated Content</h3>
                  <p>
                    The Service may use artificial intelligence and machine learning technologies to generate analysis
                    and recommendations. AI-generated content may contain errors, inaccuracies, or inappropriate
                    suggestions. You are responsible for evaluating and verifying any recommendations before
                    implementation.
                  </p>

                  <h2>13. Limitation of Liability</h2>
                  <h3>13.1 Liability Cap</h3>
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, OUR TOTAL LIABILITY TO YOU FOR ANY DAMAGES
                    ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE, WHETHER IN CONTRACT, TORT (INCLUDING
                    NEGLIGENCE), OR OTHERWISE, SHALL NOT EXCEED THE GREATER OF: (A) THE TOTAL AMOUNT YOU PAID TO US IN
                    THE 12 MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY, OR (B) ONE HUNDRED DOLLARS ($100 USD).
                  </p>
                  <h3>13.2 Exclusion of Consequential Damages</h3>
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT,
                    INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF
                    PROFITS, LOSS OF DATA, LOSS OF USE, LOSS OF GOODWILL, BUSINESS INTERRUPTION, OR OTHER INTANGIBLE
                    LOSSES, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                  </p>
                  <h3>13.3 Exclusions and Limitations</h3>
                  <p>
                    Some jurisdictions do not allow the exclusion or limitation of certain warranties or damages. In
                    such jurisdictions, our liability shall be limited to the greatest extent permitted by law.
                  </p>
                  <h3>13.4 Basis of the Bargain</h3>
                  <p>
                    You acknowledge that we have set our prices and entered into these Terms in reliance upon the
                    disclaimers of warranty and limitations of liability set forth herein, and that the same form an
                    essential basis of the bargain between you and us.
                  </p>

                  <h2>14. Indemnification</h2>
                  <p>
                    You agree to indemnify, defend, and hold harmless Torque Advisory LLC and its members, managers,
                    officers, employees, agents, and licensors from and against any and all claims, liabilities,
                    damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising from:
                  </p>
                  <ul>
                    <li>Your use or misuse of the Service</li>
                    <li>Your violation of these Terms</li>
                    <li>Your violation of any rights of another party, including intellectual property rights</li>
                    <li>Your Content or any data you upload to the Service</li>
                    <li>Any fraudulent or illegal activity</li>
                  </ul>

                  <h2>15. Dispute Resolution</h2>
                  <h3>15.1 Informal Resolution</h3>
                  <p>
                    If you have any dispute with us, you agree to first contact us at support@trainerdx.com and attempt
                    to resolve the dispute informally. We will attempt to resolve the dispute in good faith.
                  </p>
                  <h3>15.2 Governing Law</h3>
                  <p>
                    These Terms and any dispute arising out of or related to these Terms or the Service shall be
                    governed by and construed in accordance with the laws of the State of Washington, United States,
                    without regard to its conflict of law provisions.
                  </p>
                  <h3>15.3 Jurisdiction and Venue</h3>
                  <p>
                    You agree that any legal action or proceeding arising out of or related to these Terms or the
                    Service shall be brought exclusively in the state or federal courts located in Washington State,
                    USA, and you consent to the personal jurisdiction of such courts.
                  </p>
                  <h3>15.4 Small Claims Court</h3>
                  <p>
                    Notwithstanding the above, either party may bring a claim in small claims court if the claim
                    qualifies for small claims court jurisdiction.
                  </p>

                  <h2>16. International Users and Export Controls</h2>
                  <h3>16.1 Compliance with Local Laws</h3>
                  <p>
                    If you access the Service from outside the United States, you are responsible for compliance with
                    all local laws, rules, and regulations, including but not limited to data protection laws, consumer
                    protection laws, and import/export regulations.
                  </p>
                  <h3>16.2 Export Restrictions</h3>
                  <p>
                    The Service and the underlying technology may be subject to United States export control laws. You
                    agree to comply with all applicable export and re-export control laws and regulations.
                  </p>
                  <h3>16.3 Sanctions Compliance</h3>
                  <p>
                    You represent and warrant that: (a) you are not located in, under the control of, or a national or
                    resident of any country subject to U.S. embargo or sanctions; (b) you are not listed on any U.S.
                    government list of prohibited or restricted parties; and (c) you will not use the Service in
                    violation of any export restriction or embargo.
                  </p>

                  <h2>17. Privacy and Data Protection</h2>
                  <p>
                    Your privacy is important to us. Our collection, use, and disclosure of your personal information is
                    governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the
                    Service, you consent to our collection and use of your information as described in the Privacy
                    Policy.
                  </p>
                  <p>
                    For questions about data protection, privacy rights, or to exercise your rights under applicable
                    data protection laws (including GDPR for EU users), please contact support@trainerdx.com.
                  </p>

                  <h2>18. Changes to Terms</h2>
                  <h3>18.1 Modification Rights</h3>
                  <p>
                    We reserve the right to modify or replace these Terms at any time at our sole discretion. If a
                    revision is material, we will make reasonable efforts to provide at least 30 days' notice prior to
                    any new terms taking effect by posting a notice on the Website or sending you an email.
                  </p>
                  <h3>18.2 Continued Use</h3>
                  <p>
                    Your continued access to or use of the Service after any changes to these Terms become effective
                    constitutes your acceptance of the modified Terms. If you do not agree to the new Terms, you must
                    stop using the Service.
                  </p>
                  <h3>18.3 Current Version</h3>
                  <p>
                    The most current version of these Terms will be posted on the Website with the &quot;Last
                    updated&quot; date at the top of this page. We encourage you to review these Terms periodically.
                  </p>

                  <h2>19. General Provisions</h2>
                  <h3>19.1 Entire Agreement</h3>
                  <p>
                    These Terms, together with our Privacy Policy and any other legal notices or agreements published by
                    us on the Service, constitute the entire agreement between you and us regarding the Service and
                    supersede all prior or contemporaneous communications and proposals.
                  </p>
                  <h3>19.2 Severability</h3>
                  <p>
                    If any provision of these Terms is held to be invalid, illegal, or unenforceable, such provision
                    shall be modified to the minimum extent necessary to make it valid and enforceable. If modification
                    is not possible, the provision shall be severed from these Terms, and the remaining provisions shall
                    remain in full force and effect.
                  </p>
                  <h3>19.3 Waiver</h3>
                  <p>
                    Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such
                    right or provision. Any waiver of any provision of these Terms will be effective only if in writing
                    and signed by us.
                  </p>
                  <h3>19.4 Assignment</h3>
                  <p>
                    You may not assign or transfer these Terms or your Account to any third party without our prior
                    written consent. We may assign or transfer these Terms or our rights and obligations without
                    restriction. These Terms shall be binding upon and inure to the benefit of the parties and their
                    respective successors and permitted assigns.
                  </p>
                  <h3>19.5 Force Majeure</h3>
                  <p>
                    We shall not be liable for any failure or delay in performance due to circumstances beyond our
                    reasonable control, including but not limited to acts of God, war, terrorism, riots, embargoes, acts
                    of civil or military authorities, fire, floods, accidents, pandemics, strikes, or shortages of
                    transportation facilities, fuel, energy, labor, or materials.
                  </p>
                  <h3>19.6 No Third-Party Beneficiaries</h3>
                  <p>
                    These Terms are for the benefit of, and shall be enforceable by, the parties only. These Terms are
                    not intended to confer any right or benefit on any third party.
                  </p>
                  <h3>19.7 Headings</h3>
                  <p>
                    The section headings in these Terms are for convenience only and have no legal or contractual
                    effect.
                  </p>
                  <h3>19.8 Language</h3>
                  <p>
                    These Terms may be translated into other languages for your convenience. In the event of any
                    conflict between the English version and a translated version, the English version shall prevail.
                  </p>

                  <h2>20. Contact Information</h2>
                  <p>
                    If you have any questions, concerns, or complaints regarding these Terms of Service, please contact
                    us at:
                  </p>
                  <ul>
                    <li>
                      <strong>Company:</strong> Torque Advisory LLC
                    </li>
                    <li>
                      <strong>Email:</strong> support@trainerdx.com
                    </li>
                    <li>
                      <strong>Website:</strong>{" "}
                      <a href="https://www.trainerdx.com" rel="noreferrer external noopener" target="_blank">
                        https://www.trainerdx.com
                      </a>
                    </li>
                  </ul>
                  <p>
                    For data protection and privacy inquiries, please use the same contact email with the subject line
                    &quot;Privacy Request&quot;.
                  </p>

                  <hr />

                  <p>
                    <em>
                      By using TrainerDX, you acknowledge that you have read, understood, and agree to be bound by these
                      Terms of Service.
                    </em>
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default TermsAndConditions;
